import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { LogOut, ShieldAlert } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/button"
import { SectionKicker } from "@/components/shared/section-kicker"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { authApi } from "@/lib/auth-api"
import { AUTH_ME_QUERY_KEY } from "@/lib/auth-query"

export const Route = createFileRoute("/_authed/unauthorized")({
  component: UnauthorizedRoute,
})

function UnauthorizedRoute() {
  const navigate = Route.useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await authApi.logout()
    } catch {}
    await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
    await router.invalidate()
    await navigate({ to: "/login" })
  }

  return (
    <div className="grid gap-4">
      <Card className="page-intro border-primary/10" role="alert">
        <CardHeader className="relative gap-5">
          <SectionKicker icon={<ShieldAlert />}>Access Control</SectionKicker>
          <CardTitle className="max-w-2xl text-5xl wrap-break-word md:text-6xl">
            You do not have permission to open this page.
          </CardTitle>
          <CardDescription className="max-w-2xl text-base/7 wrap-break-word">
            This section is limited to guild admins. If you believe this is a
            mistake, verify your guild role setup and sign in again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              type="button"
              onClick={() => {
                void navigate({ to: "/" })
              }}
            >
              Return to dashboard
            </Button>
            <Button
              size="lg"
              type="button"
              variant="outline"
              disabled={isLoggingOut}
              onClick={() => {
                void handleLogout()
              }}
            >
              {isLoggingOut ? (
                <Spinner className="mr-2 size-4" />
              ) : (
                <LogOut className="mr-2 size-4" aria-hidden="true" />
              )}
              Log out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
